import { FadeUp } from "./Reveal";
import { Heart, Building2, Hotel, Puzzle } from "lucide-react";
import { useLang } from "./LanguageContext";

const BASE = "https://customer-assets-gfyr7b9c.emergentagent.net/job_mozzarella-show/artifacts/";
const VIDEO = `${BASE}m5ytyjy0_pentolo%201.mp4`;
const photoSrcs = [
  "/events/pent-1.webp",
  "/events/pent-2.webp",
  "/events/pent-3.webp",
  "/events/pent-4.webp",
];
const prepSrcs = [
  "/events/prep-1.webp",
  "/events/prep-2.webp",
  "/events/prep-3.webp",
];

const copy = {
  en: {
    kicker: "Grand Events & Show Cooking",
    h2: <>The show, scaled for the <em className="italic">grand occasion.</em></>,
    body: <>Beyond the table, the same craft goes large — cooked and served live from the giant pan. A format built for <strong>luxury weddings</strong>, <strong>corporate events</strong> and <strong>large resorts</strong>, where the cooking itself becomes the entertainment.</>,
    badge: "Live · On location",
    alts: ["The toss — fresh pasta turned over the flame", "A grate of lime zest through the rising steam", "Crushed pistachio, rained over the pan", "Served straight from the pan, plate after plate"],
    prepAlts: ["It begins — the soffritto meets the giant pan", "Cavatelli with swordfish and cherry tomatoes, minutes from serving", "The antipasti — burrata, crispy prawns and gratinated mussels, plated by hand"],
    formats: [
      { Icon: Heart, t: "Luxury Weddings", d: "A theatrical centerpiece your guests will remember." },
      { Icon: Building2, t: "Corporate Events", d: "A shared, high-energy moment that breaks the ice." },
      { Icon: Hotel, t: "Large Resorts", d: "Volume without compromise — cooked and served live." },
    ],
    seamless: { Icon: Puzzle, t: "Seamless Integration", d: "Minimal on-site footprint. We bring our signature copper & steel equipment — all we require is a flat presentation area and access to basic culinary prep facilities. An artistic add-on that completes your venue's catering, never replacing or competing with it." },
  },
  it: {
    kicker: "Grandi Eventi & Show Cooking",
    h2: <>Lo show, in scala per la <em className="italic">grande occasione.</em></>,
    body: <>Oltre il tavolo, lo stesso mestiere si fa grande — cucinato e servito dal vivo dal padellone. Un formato pensato per <strong>matrimoni di lusso</strong>, <strong>eventi aziendali</strong> e <strong>grandi resort</strong>, dove la cucina stessa diventa lo spettacolo.</>,
    badge: "Live · In location",
    alts: ["La saltata — pasta fresca voltata sulla fiamma", "Una grattata di zest di lime nel vapore che sale", "Granella di pistacchio, a pioggia sul padellone", "Servita direttamente dalla padella, piatto dopo piatto"],
    prepAlts: ["Si comincia — il soffritto incontra il padellone", "Cavatelli con pesce spada e pomodorini, a pochi minuti dal servizio", "Gli antipasti — burrata, gamberi croccanti e cozze gratinate, impiattati a mano"],
    formats: [
      { Icon: Heart, t: "Matrimoni di Lusso", d: "Un fulcro teatrale che gli ospiti ricorderanno." },
      { Icon: Building2, t: "Eventi Aziendali", d: "Un momento condiviso e pieno di energia che rompe il ghiaccio." },
      { Icon: Hotel, t: "Grandi Resort", d: "Volume senza compromessi — cucinato e servito dal vivo." },
    ],
    seamless: { Icon: Puzzle, t: "Integrazione Senza Intralci", d: "Ingombro minimo in loco. Portiamo noi la nostra attrezzatura signature in rame e acciaio — servono solo un'area di presentazione piana e l'accesso a una zona di preparazione di base. Un add-on artistico che completa il catering della venue, senza mai sostituirlo né competere con esso." },
  },
  ru: {
    kicker: "Большие события и шоу-кулинария",
    h2: <>Шоу в масштабе <em className="italic">большого события.</em></>,
    body: <>За пределами стола то же ремесло становится большим — готовится и подаётся вживую из гигантской сковороды. Формат для <strong>роскошных свадеб</strong>, <strong>корпоративных событий</strong> и <strong>крупных курортов</strong>.</>,
    badge: "Live · На площадке",
    alts: ["Бросок — свежая паста над огнём", "Цедра лайма сквозь поднимающийся пар", "Дроблёная фисташка дождём над сковородой", "Подача прямо со сковороды, тарелка за тарелкой"],
    prepAlts: ["Начало — соффритто встречает гигантскую сковороду", "Каватели с рыбой-меч и черри, за минуты до подачи", "Антипасти — буррата, хрустящие креветки и мидии гратен, вручную"],
    formats: [
      { Icon: Heart, t: "Роскошные свадьбы", d: "Театральный центр вечера, который запомнят гости." },
      { Icon: Building2, t: "Корпоративные события", d: "Общий яркий момент, который ломает лёд." },
      { Icon: Hotel, t: "Крупные курорты", d: "Объём без компромиссов — готовится и подаётся вживую." },
    ],
    seamless: { Icon: Puzzle, t: "Бесшовная интеграция", d: "Минимальное присутствие на площадке. Мы привозим фирменное медное и стальное оборудование — нужны лишь ровная зона презентации и доступ к базовой кухонной подготовке. Артистичное дополнение, которое завершает кейтеринг площадки, не заменяя его и не конкурируя с ним." },
  },
  es: {
    kicker: "Grandes Eventos & Show Cooking",
    h2: <>El show, a escala de la <em className="italic">gran ocasión.</em></>,
    body: <>Más allá de la mesa, el mismo oficio se hace grande — cocinado y servido en vivo desde la sartén gigante. Un formato pensado para <strong>bodas de lujo</strong>, <strong>eventos corporativos</strong> y <strong>grandes resorts</strong>, donde la cocina misma se convierte en el espectáculo.</>,
    badge: "Live · En locación",
    alts: ["El salteado — pasta fresca volteada sobre la llama", "Ralladura de lima entre el vapor que sube", "Pistacho triturado, lloviendo sobre la sartén", "Servida directo de la sartén, plato tras plato"],
    prepAlts: ["Comienza — el sofrito encuentra la sartén gigante", "Cavatelli con pez espada y tomates cherry, a minutos de servir", "Los antipasti — burrata, camarones crujientes y mejillones gratinados, emplatados a mano"],
    formats: [
      { Icon: Heart, t: "Bodas de Lujo", d: "Un centro teatral que tus invitados recordarán." },
      { Icon: Building2, t: "Eventos Corporativos", d: "Un momento compartido y lleno de energía que rompe el hielo." },
      { Icon: Hotel, t: "Grandes Resorts", d: "Volumen sin compromisos — cocinado y servido en vivo." },
    ],
    seamless: { Icon: Puzzle, t: "Integración Sin Fricciones", d: "Huella mínima en el lugar. Traemos nuestro equipo signature de cobre y acero — solo necesitamos un área de presentación plana y acceso a una zona básica de preparación. Un add-on artístico que completa el catering del lugar, sin sustituirlo ni competir con él." },
  },
};

export const GrandEvents = ({ variant = "full" }) => {
  const { lang } = useLang();
  const t = copy[lang];
  const lite = variant === "ilusha";
  const formats = lite ? [...t.formats.slice(0, 2), t.seamless] : t.formats;
  return (
    <section id="grand-events" className="py-24 md:py-40 bg-wine text-bg overflow-hidden" data-testid="grand-events-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="mb-14 md:mb-20 max-w-2xl">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-5">{t.kicker}</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">{t.h2}</h2>
          <p className="font-sans text-base md:text-lg text-bg/70 mt-8 leading-relaxed">{t.body}</p>
        </FadeUp>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-stretch">
          {!lite && (
          <FadeUp className="lg:col-span-6">
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]">
              <video
                data-testid="grand-events-video"
                src={VIDEO}
                poster="/stage/poster-pentolo.jpg"
                autoPlay
                muted
                loop
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute top-5 left-5 flex items-center gap-2 bg-wine/70 backdrop-blur-md rounded-full px-4 py-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-terracotta opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-terracotta" />
                </span>
                <span className="font-sans text-[11px] uppercase tracking-[0.25em] text-bg">{t.badge}</span>
              </div>
            </div>
          </FadeUp>
          )}

          <div className={`${lite ? "lg:col-span-12 grid-cols-2 lg:grid-cols-4" : "lg:col-span-6 grid-cols-2"} grid gap-4 md:gap-5`}>
            {photoSrcs.map((src, i) => (
              <FadeUp key={i} delay={i * 0.08}>
                <figure className="group h-full flex flex-col">
                  <div className="relative overflow-hidden rounded-sm aspect-[3/4] flex-1">
                    <img src={src} alt={t.alts[i]} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
                  </div>
                  <figcaption className="mt-3">
                    <span className="font-serif italic text-bg/75 text-sm md:text-base">{t.alts[i]}</span>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mt-4 md:mt-5" data-testid="grand-events-prep">
          {prepSrcs.map((src, i) => (
            <FadeUp key={src} delay={i * 0.08}>
              <figure className="group h-full flex flex-col">
                <div className="relative overflow-hidden rounded-sm aspect-[4/3]">
                  <img src={src} alt={t.prepAlts[i]} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
                </div>
                <figcaption className="mt-3">
                  <span className="font-serif italic text-bg/75 text-sm md:text-base">{t.prepAlts[i]}</span>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-14 md:mt-20 border-t border-bg/15 pt-12">
          {formats.map((f) => (
            <FadeUp key={f.t}>
              <f.Icon className="h-7 w-7 text-terracotta mb-4" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl md:text-3xl mb-2">{f.t}</h3>
              <p className="font-sans text-sm md:text-base text-bg/70 leading-relaxed max-w-xs">{f.d}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};
